import api from '@/lib/api';

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface BillingPortalResponse {
  url: string;
}

export interface SubscriptionPreview {
  cost: number;
  billingCycle: string;
  features: Record<string, boolean>;
}

export interface SessionVerification {
  success: boolean;
  paymentStatus: string;
  subscriptionId?: string;
  customerEmail?: string;
}

export interface PaymentMethod {
  type: string;
  card?: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  };
}

export interface BillingCycleUpdateResponse {
  message: string;
  billingCycle: string;
  nextBillingDate: string;
  amount: number;
}

export interface StripeDiscount {
  couponId: string;
  name: string | null;
  percentOff: number | null;
  amountOff: number | null;
  duration: string;
  durationInMonths: number | null;
  valid: boolean;
}

export interface StripeSubscriptionDetails {
  subscriptionId: string;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  interval: string;
  amount: number; // base price (before discount)
  effectiveAmount?: number; // discounted price if a coupon/promo applies
  currency: string;
  discount: StripeDiscount | null;
}

/**
 * Create a Stripe Checkout Session
 * Redirects user to Stripe's hosted checkout page
 */
export const createCheckoutSession = async (
  features: Record<string, boolean>,
  billingCycle: 'monthly' | 'yearly',
  promoCode?: string
): Promise<CheckoutSessionResponse> => {
  const response = await api.post('/api/stripe/create-checkout-session', {
    features,
    billingCycle,
    promoCode
  });
  return response.data;
};

/**
 * Create a Billing Portal Session
 * Allows users to manage their subscription
 */
export const createBillingPortalSession = async (): Promise<BillingPortalResponse> => {
  const response = await api.post('/api/stripe/create-portal-session');
  return response.data;
};

/**
 * Preview subscription cost without creating a session
 */
export const previewSubscription = async (
  features: Record<string, boolean>,
  billingCycle: 'monthly' | 'yearly'
): Promise<SubscriptionPreview> => {
  const response = await api.post('/api/stripe/preview-subscription', {
    features,
    billingCycle
  });
  return response.data;
};

/**
 * Verify a checkout session after redirect
 */
export const verifyCheckoutSession = async (sessionId: string): Promise<SessionVerification> => {
  const response = await api.get(`/api/stripe/verify-session/${sessionId}`);
  return response.data;
};

/**
 * Cancel subscription at period end
 */
export const cancelSubscription = async (): Promise<void> => {
  await api.post('/api/stripe/cancel-subscription');
};

/**
 * Reactivate a canceled subscription
 */
export const reactivateSubscription = async (): Promise<void> => {
  await api.post('/api/stripe/reactivate-subscription');
};

/**
 * Get upcoming invoice preview
 */
export const getUpcomingInvoice = async () => {
  const response = await api.get('/api/stripe/upcoming-invoice');
  return response.data;
};

/**
 * Get payment method details
 */
export const getPaymentMethod = async (): Promise<{ paymentMethod: PaymentMethod | null }> => {
  const response = await api.get('/api/stripe/payment-method');
  return response.data;
};

/**
 * Update billing cycle (monthly <-> yearly)
 */
export const updateBillingCycle = async (billingCycle: 'monthly' | 'yearly'): Promise<BillingCycleUpdateResponse> => {
  const response = await api.post('/api/stripe/update-billing-cycle', { billingCycle });
  return response.data;
};

/**
 * Get Stripe subscription details including discounts
 */
export const getSubscriptionDetails = async (): Promise<StripeSubscriptionDetails> => {
  const response = await api.get('/api/stripe/subscription-details');
  return response.data;
};

export default {
  createCheckoutSession,
  createBillingPortalSession,
  previewSubscription,
  verifyCheckoutSession,
  cancelSubscription,
  reactivateSubscription,
  getUpcomingInvoice,
  getPaymentMethod,
  updateBillingCycle,
  getSubscriptionDetails
};

